import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { SupplierDto } from '@/types/materials';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type {
  CreatePurchaseOrderRequest,
  PurchaseOrderDto,
  PurchaseOrderLineDto,
  PurchaseOrderLineInput,
  PurchaseRequestDto,
  UpdatePurchaseOrderRequest,
} from '@/types/purchasing';

export default function PurchaseOrdersPage() {
  const { t } = useTranslation('purchasing');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => (await apiClient.get<PurchaseOrderDto[]>('/purchase-orders')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });
  const { data: purchaseRequests = [] } = useQuery({
    queryKey: ['purchase-requests'],
    queryFn: async () => (await apiClient.get<PurchaseRequestDto[]>('/purchase-requests')).data,
    enabled: config?.purchaseRequestEnabled ?? false,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  // Still-unordered balance per PR line, keyed by materialId since there's no per-line FK from
  // PurchaseOrderLine back to PurchaseRequestLine — sums orderedQty across every sibling
  // (non-rejected) PO already raised against the same PR and subtracts it from the requested qty.
  // Shared by the PR picker's visibility filter and applyPurchaseRequest so the two never disagree.
  const getPrBalanceLines = (pr: PurchaseRequestDto) => {
    const alreadyOrderedByMaterial = new Map<string, number>();
    for (const otherPo of orders) {
      if (otherPo.purchaseRequestId !== pr.id || otherPo.status === 'Rejected') continue;
      for (const line of otherPo.lines) {
        alreadyOrderedByMaterial.set(line.materialId, (alreadyOrderedByMaterial.get(line.materialId) ?? 0) + line.orderedQty);
      }
    }
    return pr.lines
      .map((l) => ({ materialId: l.materialId, orderedQty: l.requestedQty - (alreadyOrderedByMaterial.get(l.materialId) ?? 0), unitPrice: 0 }))
      .filter((l) => l.orderedQty > 0);
  };

  // Only an Approved, non-closed PR with something still unordered can be converted into a PO —
  // Draft/Submitted/Rejected/closed ones, and ones already fully covered by other POs, are dropped.
  const openPurchaseRequests = purchaseRequests.filter(
    (pr) => pr.status === 'Approved' && !pr.isClosed && getPrBalanceLines(pr).length > 0,
  );

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<PurchaseOrderDto | null>(null);
  const [purchaseRequestId, setPurchaseRequestId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [lines, setLines] = useState<PurchaseOrderLineInput[]>([{ materialId: '', orderedQty: 0, unitPrice: 0 }]);

  const resetForm = () => {
    setEditingId(null);
    setPurchaseRequestId('');
    setWarehouseId('');
    setSupplierId('');
    setLines([{ materialId: '', orderedQty: 0, unitPrice: 0 }]);
  };

  // Selecting a Purchase Request (optional) pre-fills the warehouse and copies its still-unordered
  // balance in as editable PO lines — quantities/prices can still be changed and lines added/removed.
  const applyPurchaseRequest = (prId: string) => {
    setPurchaseRequestId(prId);
    const pr = purchaseRequests.find((p) => p.id === prId);
    if (!pr) return;
    setWarehouseId(pr.warehouseId);

    const balanceLines = getPrBalanceLines(pr);
    setLines(balanceLines.length > 0 ? balanceLines : [{ materialId: '', orderedQty: 0, unitPrice: 0 }]);
  };

  // Only usable up to the point receiving actually starts — mirrors PurchaseOrderService.UpdateAsync's guard.
  const canEdit = (r: PurchaseOrderDto) => !r.isClosed && r.lines.every((l) => l.receivedQty <= 0);

  const openEdit = (r: PurchaseOrderDto) => {
    setEditingId(r.id);
    setPurchaseRequestId('');
    setWarehouseId(r.warehouseId);
    setSupplierId(r.supplierId);
    setLines(r.lines.map((l) => ({ materialId: l.materialId, orderedQty: l.orderedQty, unitPrice: l.unitPrice })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingId) {
        const payload: UpdatePurchaseOrderRequest = { supplierId, warehouseId, expectedDate: null, lines: cleanLines };
        return apiClient.put(`/purchase-orders/${editingId}`, payload);
      }
      const payload: CreatePurchaseOrderRequest = {
        warehouseId, supplierId, purchaseRequestId: purchaseRequestId || null, expectedDate: null, lines: cleanLines,
      };
      return apiClient.post('/purchase-orders', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      setOpen(false);
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-orders/${id}/close`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
  const approveMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-orders/${id}/approve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/purchase-orders/${id}/reject`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['purchase-orders'] }),
  });

  const columns: DataTableColumn<PurchaseOrderDto>[] = [
    { field: 'poNumber', headerName: t('purchaseOrder.table.number') },
    { field: 'supplierName', headerName: t('purchaseOrder.table.supplier'), filterable: true },
    { field: 'warehouseName', headerName: t('purchaseOrder.table.warehouse'), filterable: true },
    { field: 'orderDate', headerName: t('purchaseOrder.table.date'), render: (r) => formatDate(r.orderDate) },
    { field: 'totalValue', headerName: t('purchaseOrder.table.value'), render: (r) => `₹${r.totalValue.toLocaleString()}` },
    { field: 'status', headerName: t('purchaseOrder.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: t('purchaseOrder.table.actions'), render: (r) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.PURCHASE_ORDER_MANAGE}>
            {canEdit(r) && (
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
            )}
            {r.status === 'Draft' && (
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" disabled={approveMutation.isPending} onClick={() => approveMutation.mutate(r.id)}>
                  {t('purchaseOrder.approve')}
                </Button>
                <Button size="small" variant="outlined" color="error" disabled={rejectMutation.isPending} onClick={() => rejectMutation.mutate(r.id)}>
                  {t('purchaseOrder.reject')}
                </Button>
              </Stack>
            )}
            {r.isClosed ? (
              <StatusChip status="Closed" />
            ) : r.status === 'PartiallyReceived' ? (
              // Close is only for a stalled partial receipt (some qty received, some short) —
              // an untouched Approved order has nothing to retire yet, and a fully/excess-received
              // one is already done (Status flips to "Received" on its own).
              <Tooltip title={t('purchaseOrder.closeHint')}>
                <Checkbox size="small" disabled={closeMutation.isPending} onChange={() => closeMutation.mutate(r.id)} />
              </Tooltip>
            ) : null}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<PurchaseOrderLineDto>[] = [
    { field: 'materialCode', headerName: t('purchaseOrder.table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'orderedQty', headerName: t('purchaseOrder.table.orderedQty') },
    { field: 'unitPrice', headerName: t('purchaseOrder.table.unitPrice'), render: (l) => `₹${l.unitPrice.toLocaleString()}` },
    { field: 'receivedQty', headerName: t('purchaseOrder.table.receivedQty') },
    { field: 'lineValue', headerName: t('purchaseOrder.table.lineValue'), render: (l) => `₹${(l.orderedQty * l.unitPrice).toLocaleString()}` },
  ];

  return (
    <>
      <PageHeader
        title={t('purchaseOrder.title')}
        subtitle={t('purchaseOrder.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.PURCHASE_ORDER_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('purchaseOrder.add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={orders} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="purchase-orders" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('purchaseOrder.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editingId && config?.purchaseRequestEnabled && (
              <SearchableSelect
                label={t('form.linkedPR')} value={purchaseRequestId} onChange={applyPurchaseRequest} fullWidth
                options={[
                  { value: '', label: t('form.none') },
                  ...openPurchaseRequests.map((pr) => ({ value: pr.id, label: `${pr.prNumber} — ${pr.warehouseName}` })),
                ]}
              />
            )}
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
              <SearchableSelect
                label={t('form.supplier')} value={supplierId} onChange={setSupplierId} fullWidth
                options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              />
            </Stack>

            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <TextField
                  type="number" label={t('form.quantity')} value={line.orderedQty} size="small" sx={{ minWidth: 110 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, orderedQty: Number(e.target.value) } : l)))}
                />
                <TextField
                  type="number" label={t('form.unitPrice')} value={line.unitPrice} size="small" sx={{ minWidth: 110 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, unitPrice: Number(e.target.value) } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLines((prev) => [...prev, { materialId: '', orderedQty: 0, unitPrice: 0 }])}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!warehouseId || !supplierId || lines.some((l) => !l.materialId || l.orderedQty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.poNumber ?? ''}
        subtitle={viewing ? `${viewing.supplierName} — ${viewing.warehouseName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
